/**
 * PhoneAuthComponent - Modern Phone Authentication UI
 *
 * Features:
 * - Clean, professional UI matching Befret design
 * - Real-time state management via PhoneAuthService
 * - OTP input with individual digit boxes
 * - Auto-focus and paste support for OTP
 * - Resend cooldown timer display
 * - Comprehensive error handling
 * - Full i18n support
 * - Accessibility (ARIA labels, keyboard navigation)
 */

import {
  Component,
  OnInit,
  OnDestroy,
  ViewChildren,
  QueryList,
  ElementRef,
  AfterViewInit
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { unlink } from '@angular/fire/auth';
import { returnUrlTreeWith, safeInternalReturnUrl } from '../../../core/utils/return-url.util';
import { PhoneAuthService, PhoneAuthState, PhonePrefix } from '../../../core/services/phone-auth.service';
import { AuthService } from '../../../core/services/auth.service';

import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { SharedModule } from '../../../shared/shared.module';

@Component({
    selector: 'app-phone-auth',
    templateUrl: './phone-auth.component.html',
    styleUrls: ['./phone-auth.component.css'],
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, RouterModule, SharedModule]
})
export class PhoneAuthComponent implements OnInit, OnDestroy, AfterViewInit {

  // Forms
  phoneForm!: FormGroup;
  emailForm!: FormGroup;
  passwordForm!: FormGroup;
  otpDigits: string[] = ['', '', '', '', '', ''];

  // State
  state!: PhoneAuthState;
  showPassword: boolean = false;

  // Subscriptions
  private destroy$ = new Subject<void>();

  // OTP input references
  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  // Navigation
  private returnUrl: string = '/';
  private restoreFlag: string | null = null;

  // OAuth Linking (when coming from Google/Facebook login that detected existing phone account)
  private linkOAuthProvider: 'google' | 'facebook' | null = null;
  private linkOAuthEmail: string | null = null;

  // Email/Password Linking (when coming from email/password login that detected existing phone account)
  private linkEmailPassword: boolean = false;

  // Phone Update Mode (when user wants to update their phone number from profile)
  private updatePhoneMode: boolean = false;
  private currentPhoneNumber: string | null = null;

  // Add Phone Mode (when user wants to add phone to their account from profile)
  private addPhoneMode: boolean = false;

  constructor(
    private fb: FormBuilder,
    private phoneAuthService: PhoneAuthService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  // ============================================================================
  // LIFECYCLE
  // ============================================================================

  ngOnInit(): void {
    this.initializeForm();
    this.subscribeToState();
    this.captureQueryParams();
  }

  ngAfterViewInit(): void {
    // Initialize reCAPTCHA after view is ready
    setTimeout(() => {
      this.initRecaptcha();
    }, 100);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Don't reset the service here to preserve state if user goes back
  }

  // ============================================================================
  // GETTERS
  // ============================================================================

  get phonePrefixes(): PhonePrefix[] {
    return this.phoneAuthService.dpdPhonePrefixes;
  }

  get isPhoneStep(): boolean {
    return this.state?.step === 'phone_input';
  }

  get isAccountCheckStep(): boolean {
    return this.state?.step === 'account_check';
  }

  get isEmailLinkStep(): boolean {
    return this.state?.step === 'email_link';
  }

  get isPasswordInputStep(): boolean {
    return this.state?.step === 'password_input';
  }

  get isExistingProviderLoginStep(): boolean {
    return this.state?.step === 'existing_provider_login';
  }

  get isOtpStep(): boolean {
    return this.state?.step === 'otp_verification';
  }

  get isSuccess(): boolean {
    return this.state?.step === 'success';
  }

  get formattedCooldown(): string {
    const seconds = this.state?.resendCooldown || 0;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs}s`;
  }

  get otpValue(): string {
    return this.otpDigits.join('');
  }

  get isOtpComplete(): boolean {
    return this.otpValue.length === 6 && this.otpDigits.every(d => /^\d$/.test(d));
  }

  get errorTranslationKey(): string {
    return this.state?.error?.translationKey || 'AUTH.PHONE.ERRORS.GENERIC';
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  private initializeForm(): void {
    const defaultPrefix = this.phoneAuthService.defaultPrefix;

    this.phoneForm = this.fb.group({
      phonePrefix: [defaultPrefix?.value || '+32', Validators.required],
      phoneNumber: ['', [Validators.required, Validators.minLength(6), Validators.pattern(/^\d+$/)]]
    });

    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });

    this.passwordForm = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(6)]]
    });
  }

  private subscribeToState(): void {
    this.phoneAuthService.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.state = state;

        // Handle success - navigate away
        if (state.step === 'success') {
          this.handleAuthSuccess();
        }
      });
  }

  private captureQueryParams(): void {
    this.returnUrl = safeInternalReturnUrl(this.route.snapshot.queryParams['returnUrl'], '/');
    this.restoreFlag = this.route.snapshot.queryParams['restore'] || null;

    // Check for OAuth linking mode (coming from Google/Facebook login)
    const linkOAuth = this.route.snapshot.queryParams['linkOAuth'];
    if (linkOAuth === 'google' || linkOAuth === 'facebook') {
      this.linkOAuthProvider = linkOAuth;
      this.linkOAuthEmail = this.route.snapshot.queryParams['email'] || null;

      console.log(`📱 [PhoneAuth] OAuth linking mode detected: ${linkOAuth}`);
      console.log(`📱 [PhoneAuth] Pending OAuth email: ${this.linkOAuthEmail}`);

      // Check if there's a pending OAuth credential in authService
      if (this.authService.hasPendingOAuthCredential()) {
        console.log('✅ [PhoneAuth] Pending OAuth credential found - will link after phone auth');
      } else {
        console.warn('⚠️ [PhoneAuth] No pending OAuth credential found - may have expired');
      }
    }

    // Check for Email/Password linking mode (coming from email/password login)
    const linkEmail = this.route.snapshot.queryParams['linkEmail'];
    if (linkEmail === 'true') {
      this.linkEmailPassword = true;
      const pendingEmail = this.route.snapshot.queryParams['email'] || null;

      console.log(`📱 [PhoneAuth] Email/Password linking mode detected`);
      console.log(`📱 [PhoneAuth] Pending email: ${pendingEmail}`);

      // Check if there's a pending email/password in authService
      if (this.authService.hasPendingEmailPassword()) {
        console.log('✅ [PhoneAuth] Pending email/password found - will link after phone auth');
      } else {
        console.warn('⚠️ [PhoneAuth] No pending email/password found - may have expired');
      }
    }

    // Check for Phone Update mode (coming from user profile - lost phone scenario)
    const updatePhone = this.route.snapshot.queryParams['updatePhone'];
    if (updatePhone === 'true') {
      this.updatePhoneMode = true;
      this.currentPhoneNumber = this.route.snapshot.queryParams['currentPhone'] || null;

      console.log(`📱 [PhoneAuth] Phone UPDATE mode detected`);
      console.log(`📱 [PhoneAuth] Current phone to replace: ${this.currentPhoneNumber}`);
    }

    // Check for Add Phone mode (coming from user profile - add phone to existing account)
    const addPhone = this.route.snapshot.queryParams['addPhone'];
    if (addPhone === 'true') {
      this.addPhoneMode = true;

      console.log(`📱 [PhoneAuth] Phone ADD mode detected`);
      console.log(`📱 [PhoneAuth] Will add phone to existing account`);
    }
  }

  private async initRecaptcha(): Promise<void> {
    try {
      await this.phoneAuthService.initializeRecaptcha('recaptcha-container');
    } catch (error) {
      console.error('Failed to initialize reCAPTCHA:', error);
    }
  }

  // ============================================================================
  // ACTIONS
  // ============================================================================

  /**
   * STEP 0: Check if phone exists before sending OTP
   *
   * Special modes:
   * - updatePhoneMode: User is updating their phone number (lost phone scenario)
   * - addPhoneMode: User is adding phone to their account (has Google/Email only)
   *
   * In these modes, user is ALREADY AUTHENTICATED, so we use linkWithPhoneNumber
   */
  async onSendCode(): Promise<void> {
    if (this.phoneForm.invalid || this.state.isLoading) {
      this.markFormTouched();
      return;
    }

    const prefix = this.phoneForm.get('phonePrefix')?.value;
    const number = this.phoneForm.get('phoneNumber')?.value;

    // SPECIAL CASE: Update Phone Mode (user lost phone, wants to update number)
    if (this.updatePhoneMode) {
      console.log('📱 [PhoneAuth] UPDATE PHONE MODE - User is already authenticated');
      console.log('📱 [PhoneAuth] Will unlink old phone and link new one');

      // First, unlink the old phone provider
      await this.unlinkOldPhoneAndLinkNew(prefix, number);
      return;
    }

    // SPECIAL CASE: Add Phone Mode (user wants to add phone to existing account)
    if (this.addPhoneMode) {
      console.log('📱 [PhoneAuth] ADD PHONE MODE - User is already authenticated');
      console.log('📱 [PhoneAuth] Will link phone to existing account');

      // Set link mode and send OTP using linkWithPhoneNumber
      await this.linkPhoneToExistingAccount(prefix, number);
      return;
    }

    // NORMAL FLOW: Check phone exists (will update state to account_check, otp_verification, or existing_provider_login)
    const linkState = await this.phoneAuthService.checkPhoneExists(prefix, number);

    // Only send OTP automatically if:
    // 1. Mode is 'existing' AND
    // 2. Step is 'otp_verification' (user has phone provider, can sign in directly)
    //
    // Do NOT send OTP if step is 'existing_provider_login' because user must
    // authenticate with their existing provider (Google/Email) first
    if (linkState?.mode === 'existing' && this.state.step === 'otp_verification') {
      console.log('📱 [PhoneAuth] MODE EXISTING with phone provider - Sending OTP automatically');
      await this.phoneAuthService.sendOTP(prefix, number);
    } else if (linkState?.mode === 'existing' && this.state.step === 'existing_provider_login') {
      console.log('🔗 [PhoneAuth] MODE EXISTING but needs to login with existing provider first - NOT sending OTP');
      // User will see the existing_provider_login step and click to login with Google/Email
    }
  }

  /**
   * Unlink old phone provider and link new phone number
   * Used when user lost their phone and wants to update to a new number
   */
  private async unlinkOldPhoneAndLinkNew(prefix: string, number: string): Promise<void> {
    try {
      const currentUser = await this.authService.getCurrentFirebaseUser();

      if (!currentUser) {
        console.error('❌ [PhoneAuth] No authenticated user for phone update');
        this.phoneAuthService['updateState']({
          error: {
            code: 'not-authenticated',
            message: 'You must be logged in to update your phone number',
            translationKey: 'AUTH.PHONE.ERRORS.NOT_AUTHENTICATED'
          }
        });
        return;
      }

      // Check if user has phone provider to unlink
      const hasPhone = currentUser.providerData?.some(p => p?.providerId === 'phone');

      if (hasPhone) {
        console.log('🔓 [PhoneAuth] Unlinking old phone provider...');
        try {
          await unlink(currentUser, 'phone');
          console.log('✅ [PhoneAuth] Old phone provider unlinked');
        } catch (unlinkError: any) {
          // If unlink fails, it might be because it's the only provider
          // In this case, we should NOT proceed
          console.error('❌ [PhoneAuth] Failed to unlink phone:', unlinkError);

          if (unlinkError.code === 'auth/no-such-provider') {
            // Phone provider doesn't exist, continue with linking new one
            console.log('ℹ️ [PhoneAuth] No phone provider to unlink, proceeding with new link');
          } else {
            this.phoneAuthService['updateState']({
              error: {
                code: 'unlink-failed',
                message: 'Failed to unlink old phone number',
                translationKey: 'AUTH.PHONE.ERRORS.UNLINK_FAILED'
              }
            });
            return;
          }
        }
      }

      // Now link the new phone number
      await this.linkPhoneToExistingAccount(prefix, number);

    } catch (error) {
      console.error('❌ [PhoneAuth] Error in unlinkOldPhoneAndLinkNew:', error);
    }
  }

  /**
   * Link phone number to existing authenticated account
   * Used for both updatePhoneMode and addPhoneMode
   */
  private async linkPhoneToExistingAccount(prefix: string, number: string): Promise<void> {
    // Set the link state to 'link' mode so sendOTP uses linkWithPhoneNumber
    this.phoneAuthService['updateState']({
      linkState: {
        mode: 'link',
        phoneNumber: `${prefix}${number.replace(/\s/g, '').replace(/^0+/, '')}`
      },
      step: 'otp_verification'
    });

    // Send OTP - since mode is 'link' and user is authenticated, it will use linkWithPhoneNumber
    await this.phoneAuthService.sendOTP(prefix, number);
  }

  /**
   * User clicked "YES, I have an account"
   */
  onUserHasAccount(): void {
    this.phoneAuthService.setUserHasAccount();
  }

  /**
   * User clicked "NO, new account"
   */
  async onUserNewAccount(): Promise<void> {
    this.phoneAuthService.setUserNewAccount();
    // Now send OTP
    const prefix = this.phoneForm.get('phonePrefix')?.value;
    const number = this.phoneForm.get('phoneNumber')?.value;
    await this.phoneAuthService.sendOTP(prefix, number);
  }

  /**
   * User entered email to link account
   */
  async onSubmitEmail(): Promise<void> {
    if (this.emailForm.invalid || this.state.isLoading) {
      this.emailForm.markAllAsTouched();
      return;
    }

    const email = this.emailForm.get('email')?.value;
    // findProfileByEmail will redirect to password_input step if profile found
    await this.phoneAuthService.findProfileByEmail(email);
  }

  /**
   * User entered password to authenticate before linking
   */
  async onSubmitPassword(): Promise<void> {
    if (this.passwordForm.invalid || this.state.isLoading) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    const password = this.passwordForm.get('password')?.value;
    const success = await this.phoneAuthService.authenticateWithEmailAndPassword(password);

    if (success) {
      // Service will update state to otp_verification step
      // Now send OTP to link phone number
      const prefix = this.phoneForm.get('phonePrefix')?.value;
      const number = this.phoneForm.get('phoneNumber')?.value;
      await this.phoneAuthService.sendOTP(prefix, number);
    }
  }

  async onVerifyCode(): Promise<void> {
    if (!this.isOtpComplete || this.state.isLoading) {
      return;
    }

    const user = await this.phoneAuthService.verifyOTP(this.otpValue);

    if (user) {
      // Success is handled via state subscription
    }
  }

  async onResendCode(): Promise<void> {
    if (!this.state.canResend || this.state.isLoading) {
      return;
    }

    // Reset OTP inputs
    this.clearOtpInputs();

    await this.phoneAuthService.resendOTP();
  }

  onBackToPhone(): void {
    this.phoneAuthService.resetToPhoneInput();
    this.clearOtpInputs();
  }

  onBackToAccountCheck(): void {
    // Go back to account check step
    const currentLinkState = this.state.linkState;
    if (currentLinkState) {
      this.phoneAuthService['updateState']({
        step: 'account_check',
        linkState: { ...currentLinkState, mode: 'new' }
      });
    }
  }

  onBackToEmailLink(): void {
    // Go back to email input step
    this.passwordForm.reset();
    this.phoneAuthService['updateState']({
      step: 'email_link',
      password: ''
    });
  }

  onBackToEmail(): void {
    this.phoneAuthService.reset();
    this.router.navigate(['/auth'], {
      queryParams: {
        returnUrl: this.returnUrl,
        ...(this.restoreFlag && { restore: this.restoreFlag })
      }
    });
  }

  /**
   * User has Google auth only - sign in with Google then link phone
   */
  async onGoogleSignIn(): Promise<void> {
    console.log('🔐 [PhoneAuth] Initiating Google sign-in for phone linking...');

    try {
      this.phoneAuthService['updateState']({ isLoading: true, error: null });

      // Sign in with Google
      const result = await this.authService.googleLogin();

      // Check if googleLogin returned a needsLinking result
      if (result && 'needsLinking' in result && result.needsLinking) {
        console.log('⚠️ [PhoneAuth] Google login returned needsLinking - unexpected scenario');
        this.phoneAuthService['updateState']({
          isLoading: false,
          error: {
            code: 'account-conflict',
            message: 'This Google account is linked to a different profile.',
            translationKey: 'AUTH.PHONE.ERRORS.ACCOUNT_CONFLICT'
          }
        });
        return;
      }

      console.log('✅ [PhoneAuth] Google sign-in successful, now linking phone...');

      // Now send OTP to link phone number (user is now authenticated)
      const prefix = this.phoneForm.get('phonePrefix')?.value;
      const number = this.phoneForm.get('phoneNumber')?.value;

      // Update link state to 'link' mode since user is now authenticated
      const currentLinkState = this.state.linkState;
      if (currentLinkState) {
        this.phoneAuthService['updateState']({
          linkState: { ...currentLinkState, mode: 'link' },
          isLoading: false
        });
      }

      // Send OTP for linking
      await this.phoneAuthService.sendOTP(prefix, number);

    } catch (error) {
      console.error('❌ [PhoneAuth] Google sign-in failed:', error);
      this.phoneAuthService['updateState']({
        isLoading: false,
        error: {
          code: 'google-signin-failed',
          message: 'Google sign-in failed',
          translationKey: 'AUTH.PHONE.ERRORS.GOOGLE_SIGNIN_FAILED'
        }
      });
    }
  }

  /**
   * Login with existing provider (Google or Email) then link phone
   * This is called when phone is found in profile but user doesn't have phone provider yet
   */
  async onLoginWithExistingProvider(): Promise<void> {
    const existingProvider = this.state.linkState?.existingProvider;
    console.log(`🔐 [PhoneAuth] Logging in with existing provider: ${existingProvider}`);

    if (existingProvider === 'google') {
      await this.onGoogleSignInForLinking();
    } else if (existingProvider === 'email') {
      // Go to password input step
      this.phoneAuthService['updateState']({
        step: 'password_input',
        email: this.state.linkState?.existingProfile?.email || ''
      });
    }
  }

  /**
   * Google sign-in specifically for linking phone (from existing_provider_login step)
   */
  private async onGoogleSignInForLinking(): Promise<void> {
    console.log('🔐 [PhoneAuth] Google sign-in for linking phone to existing profile...');

    try {
      this.phoneAuthService['updateState']({ isLoading: true, error: null });

      // Sign in with Google
      const result = await this.authService.googleLogin();

      // Check if googleLogin returned a needsLinking result
      // This can happen if the Google account email matches an existing profile with different provider
      if (result && 'needsLinking' in result && result.needsLinking) {
        console.log('⚠️ [PhoneAuth] Google login returned needsLinking - unexpected scenario');
        console.log('📊 [PhoneAuth] Link state:', result.linkState);

        // This is an edge case - the user's Google email is associated with another profile
        // that wasn't created via their phone number. Show error and let them try again.
        this.phoneAuthService['updateState']({
          isLoading: false,
          error: {
            code: 'account-conflict',
            message: 'This Google account is linked to a different profile. Please use the correct account.',
            translationKey: 'AUTH.PHONE.ERRORS.ACCOUNT_CONFLICT'
          }
        });
        return;
      }

      console.log('✅ [PhoneAuth] Google sign-in successful, now sending OTP for linking...');

      // Now send OTP to link phone number (user is now authenticated)
      const prefix = this.phoneForm.get('phonePrefix')?.value;
      const number = this.phoneForm.get('phoneNumber')?.value;

      // Update link state to 'link' mode since user is now authenticated
      const currentLinkState = this.state.linkState;
      if (currentLinkState) {
        this.phoneAuthService['updateState']({
          linkState: { ...currentLinkState, mode: 'link' },
          isLoading: false
        });
      }

      // Send OTP for linking
      await this.phoneAuthService.sendOTP(prefix, number);

    } catch (error) {
      console.error('❌ [PhoneAuth] Google sign-in failed:', error);
      this.phoneAuthService['updateState']({
        isLoading: false,
        error: {
          code: 'google-signin-failed',
          message: 'Google sign-in failed',
          translationKey: 'AUTH.PHONE.ERRORS.GOOGLE_SIGNIN_FAILED'
        }
      });
    }
  }

  /**
   * Go back to phone input from existing_provider_login step
   */
  onBackFromExistingProvider(): void {
    this.phoneAuthService.resetToPhoneInput();
  }

  // ============================================================================
  // OTP INPUT HANDLING
  // ============================================================================

  onOtpInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Only accept single digit
    if (value.length > 0) {
      const digit = value.slice(-1);
      if (/^\d$/.test(digit)) {
        this.otpDigits[index] = digit;
        input.value = digit;

        // Auto-advance to next input
        if (index < 5) {
          this.focusOtpInput(index + 1);
        } else {
          // Last digit - auto-verify if complete
          if (this.isOtpComplete) {
            this.onVerifyCode();
          }
        }
      } else {
        input.value = this.otpDigits[index];
      }
    }
  }

  onOtpKeydown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;

    // Handle backspace
    if (event.key === 'Backspace') {
      if (!input.value && index > 0) {
        // Move to previous input
        this.otpDigits[index - 1] = '';
        this.focusOtpInput(index - 1);
        event.preventDefault();
      } else {
        this.otpDigits[index] = '';
      }
    }

    // Handle arrow keys
    if (event.key === 'ArrowLeft' && index > 0) {
      this.focusOtpInput(index - 1);
      event.preventDefault();
    }
    if (event.key === 'ArrowRight' && index < 5) {
      this.focusOtpInput(index + 1);
      event.preventDefault();
    }

    // Handle Enter
    if (event.key === 'Enter' && this.isOtpComplete) {
      this.onVerifyCode();
      event.preventDefault();
    }
  }

  onOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text') || '';
    const digits = pastedData.replace(/\D/g, '').slice(0, 6);

    if (digits.length > 0) {
      // Fill all inputs
      for (let i = 0; i < 6; i++) {
        this.otpDigits[i] = digits[i] || '';
      }

      // Update input values
      setTimeout(() => {
        this.otpInputs.forEach((input, i) => {
          input.nativeElement.value = this.otpDigits[i];
        });

        // Focus last filled input or verify
        const lastFilledIndex = Math.min(digits.length - 1, 5);
        if (digits.length === 6) {
          this.onVerifyCode();
        } else {
          this.focusOtpInput(lastFilledIndex + 1);
        }
      });
    }
  }

  private focusOtpInput(index: number): void {
    setTimeout(() => {
      const inputs = this.otpInputs?.toArray();
      if (inputs && inputs[index]) {
        inputs[index].nativeElement.focus();
        inputs[index].nativeElement.select();
      }
    });
  }

  private clearOtpInputs(): void {
    this.otpDigits = ['', '', '', '', '', ''];
    setTimeout(() => {
      this.otpInputs?.forEach(input => {
        input.nativeElement.value = '';
      });
      this.focusOtpInput(0);
    });
  }

  // ============================================================================
  // HELPERS
  // ============================================================================

  private markFormTouched(): void {
    Object.keys(this.phoneForm.controls).forEach(key => {
      this.phoneForm.get(key)?.markAsTouched();
    });
  }

  private async handleAuthSuccess(): Promise<void> {
    // Small delay for user to see success state
    setTimeout(async () => {
      // Check if we need to link a pending OAuth credential (Google/Facebook)
      if (this.linkOAuthProvider && this.authService.hasPendingOAuthCredential()) {
        console.log(`🔗 [PhoneAuth] Linking pending ${this.linkOAuthProvider} credential after phone auth...`);

        try {
          const linkResult = await this.authService.linkPendingOAuthCredential();

          if (linkResult.success) {
            console.log(`✅ [PhoneAuth] Successfully linked ${this.linkOAuthProvider} provider!`);
            console.log(`📊 [PhoneAuth] Updated providers: ${linkResult.authProviders?.join(', ')}`);
          } else {
            console.error(`❌ [PhoneAuth] Failed to link ${this.linkOAuthProvider}:`, linkResult.error);
            // Don't block navigation - user is still authenticated
          }
        } catch (error) {
          console.error(`❌ [PhoneAuth] Error linking ${this.linkOAuthProvider}:`, error);
          // Don't block navigation - user is still authenticated
        }

        // Clear the OAuth link state
        this.authService.clearOAuthLinkState();
      }

      // Check if we need to link pending email/password
      if (this.linkEmailPassword && this.authService.hasPendingEmailPassword()) {
        console.log(`🔗 [PhoneAuth] Linking pending email/password after phone auth...`);

        try {
          const linkResult = await this.authService.linkPendingEmailPassword();

          if (linkResult.success) {
            console.log(`✅ [PhoneAuth] Successfully linked email/password provider!`);
            console.log(`📊 [PhoneAuth] Updated providers: ${linkResult.authProviders?.join(', ')}`);
          } else {
            console.error(`❌ [PhoneAuth] Failed to link email/password:`, linkResult.error);
            // Don't block navigation - user is still authenticated
          }
        } catch (error) {
          console.error(`❌ [PhoneAuth] Error linking email/password:`, error);
          // Don't block navigation - user is still authenticated
        }

        // Clear the pending email/password
        this.authService.clearPendingEmailPassword();
        this.authService.clearOAuthLinkState();
      }

      // Check if profile is complete
      console.log('🔍 [PhoneAuth] handleAuthSuccess - checking profileComplete:', this.state.profileComplete);
      console.log('🔍 [PhoneAuth] handleAuthSuccess - full state:', JSON.stringify({
        step: this.state.step,
        profileComplete: this.state.profileComplete,
        linkState: this.state.linkState
      }));

      if (!this.state.profileComplete) {
        // Redirect to complete profile page
        console.log('📝 [PhoneAuth] Profile incomplete, redirecting to complete-profile');
        this.router.navigate(['/auth/complete-profile'], {
          queryParams: {
            returnUrl: this.returnUrl,
            phone: this.state.formattedPhoneNumber
          }
        });
      } else {
        // Profile complete, go to return URL
        console.log('✅ [PhoneAuth] Profile complete, redirecting to:', this.returnUrl);
        if (this.restoreFlag) {
          // #1160 — cf. `returnUrlTreeWith` : `navigate([returnUrl], …)`
          // traiterait le returnUrl comme un segment unique et encoderait ses
          // propres query params (`/details%3FtrackingID=…` → 404).
          this.router.navigateByUrl(returnUrlTreeWith(this.router, this.returnUrl, { restore: true }));
        } else {
          this.router.navigateByUrl(this.returnUrl);
        }
      }
    }, 500);
  }

  // For template - tracking function
  trackByIndex(index: number): number {
    return index;
  }
}
